import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, money, date, dateTime } from '../api.js';
import { useAuth } from '../App.jsx';

/**
 * Разчетени доставни фактури.
 *
 * Качва се файл, програмата вади редовете и ги свързва със стоки,
 * колкото може. Останалото се посочва на ръка веднъж и се помни.
 */
export default function InvoiceImports() {
  const { user } = useAuth();
  const canWrite = user.role !== 'auditor';
  const navigate = useNavigate();

  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileInput = useRef(null);

  function load() {
    api.get('/api/invoice-imports').then((r) => setItems(r.items)).catch((e) => setError(e.message));
  }
  useEffect(load, []);

  async function upload(file) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const res = await fetch('/api/invoice-imports', {
        method: 'POST', credentials: 'same-origin', body: form,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error ?? `Грешка ${res.status}`);
      navigate(`/invoice-imports/${data.id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Разчитане на доставни фактури</h1>
        <div className="spacer" />
        {canWrite && (
          <>
            <input ref={fileInput} type="file" style={{ display: 'none' }}
              accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp"
              onChange={(e) => upload(e.target.files?.[0])} />
            <button className="primary" disabled={busy}
              onClick={() => fileInput.current?.click()}>
              {busy ? 'Разчитане…' : 'Качи фактура'}
            </button>
          </>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {busy && (
        <div className="card"><div className="card-body">
          Файлът се разчита. Сканираните страници отнемат по няколко секунди.
        </div></div>
      )}

      <div className="card">
        <table className="compact">
          <thead>
            <tr>
              <th>Файл</th>
              <th>Доставчик</th>
              <th>Фактура</th>
              <th className="num">Сума</th>
              <th className="num">Редове</th>
              <th>Разчетено от</th>
              <th>Състояние</th>
              <th>Качено</th>
            </tr>
          </thead>
          <tbody>
            {!items && <tr><td colSpan={8} className="empty">Зареждане…</td></tr>}
            {items?.length === 0 && (
              <tr><td colSpan={8} className="empty">
                Още няма качени фактури
              </td></tr>
            )}
            {items?.map((r) => (
              <tr key={r.id}>
                <td><Link to={`/invoice-imports/${r.id}`}>{r.file_name}</Link></td>
                <td>{r.partner_name ?? <span className="muted">не е посочен</span>}</td>
                <td>{r.invoice_no ?? '—'}{r.invoice_date ? ` / ${date(r.invoice_date)}` : ''}</td>
                <td className="num">{r.total_amount != null ? money(r.total_amount, r.currency) : '—'}</td>
                <td className="num">
                  {r.line_count}
                  {r.unmatched > 0 && (
                    <span className="badge warn" style={{ marginLeft: 6 }}>
                      {r.unmatched} несвързани
                    </span>
                  )}
                </td>
                <td><SourceBadge source={r.source} confidence={r.ocr_confidence} /></td>
                <td><StatusBadge row={r} /></td>
                <td className="muted">{dateTime(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="hint">
        PDF с текстов слой се чете направо и е точен. Сканираното минава през
        разчитане и може да сгреши — затова се преглежда, преди да стане доставка.
      </p>
    </>
  );
}

export function SourceBadge({ source, confidence }) {
  if (source === 'pdf-text') {
    return <span className="badge ok" title="Прочетено от текста на PDF-а">точен текст</span>;
  }
  const c = confidence != null ? Math.round(Number(confidence)) : null;
  const kind = c == null ? 'muted' : c >= 88 ? 'ok' : c >= 75 ? 'warn' : 'danger';
  return (
    <span className={`badge ${kind}`} title="Разчетено от изображение">
      разчетено{c != null ? ` ${c}%` : ''}
    </span>
  );
}

export function StatusBadge({ row }) {
  if (row.status === 'confirmed') {
    return row.document_id
      ? <Link to={`/documents/${row.document_id}`}><span className="badge ok">доставка</span></Link>
      : <span className="badge ok">потвърдено</span>;
  }
  if (row.unmatched > 0) return <span className="badge warn">за преглед</span>;
  return <span className="badge">готово за доставка</span>;
}
